import React, { useState } from 'react';

import { SkaterInfo, SkatingPassData } from '@types';
import { ProgressIndicator } from '_atoms';
import { useAppSelector } from '_hooks';
import {
  BookingSubmissionStatus,
  ParticipantsInfo,
  PassConfirmation,
  Payment,
  SelectSkatingPass,
  SkatingPassPreview,
  WaiverNew,
} from '_molecules';
import { SkatingPassService } from '_services';
import HelperMethods from '_utils/HelperMethods';

export type SkatingPassStep =
  | 'selectSkatingPass'
  | 'participants'
  | 'waiver'
  | 'payment'
  | 'confirmation';

const STEPS = [
  { id: 'selectSkatingPass', label: 'Select Pass' },
  { id: 'participants', label: 'Participant' },
  { id: 'waiver', label: 'Waiver' },
  { id: 'payment', label: 'Payment' },
];

const SkatingPassFlow: React.FC = () => {
  const ticketBarcode = HelperMethods.generateRandom6DigitNumber();

  // All hooks must be called before any early returns
  const [currentStep, setCurrentStep] =
    useState<SkatingPassStep>('selectSkatingPass');
  const [passData, setPassData] = useState<SkatingPassData>({
    barcodeId: `sp${ticketBarcode.toString()}`,
    skaterWaivers: [],
  });
  const [submitError, setSubmitError] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);

  // Get current user from Redux store
  const currentUser = useAppSelector(state => state.auth.currentUser);
  const accountDetails = useAppSelector(state => state.auth.accountDetails);
  const isAuthenticated = useAppSelector(state => state.auth.isAuthenticated);

  // Create skater info from logged-in user based on pass type
  const getUserSkaterInfo = (passType?: 'adult' | 'child'): SkaterInfo => {
    const user = currentUser || accountDetails;
    const isChild = passType === 'child';
    const isAdult = passType === 'adult';

    return {
      id: '1',
      firstName: user?.first_name || user?.givenName || '',
      lastName: user?.last_name || user?.familyName || '',
      email: user?.email || '',
      phoneNumber: user?.phone_number || '',
      dateOfBirth: user?.date_of_birth || '',
      isMinor: isChild,
      isAdult,
      isChild,
    };
  };

  // Show authentication required if user is not logged in
  if (!isAuthenticated || (!currentUser && !accountDetails)) {
    return (
      <div className="max-w-6xl mx-auto p-0 md:p-6">
        <SkatingPassPreview />
      </div>
    );
  }

  const handleNext = (stepData?: Partial<SkatingPassData>) => {
    if (stepData) {
      setPassData(prev => {
        const updatedData = { ...prev, ...stepData };

        // Update skater info based on selected pass type
        if (updatedData.selectedPassType) {
          updatedData.skaterInfo = getUserSkaterInfo(
            updatedData.selectedPassType,
          );
        }

        return updatedData;
      });
    }

    const currentIndex = STEPS.findIndex(step => step.id === currentStep);

    if (currentIndex < STEPS.length - 1) {
      setCurrentStep(STEPS[currentIndex + 1].id as SkatingPassStep);
      // Scroll to top for mobile users
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  const handlePrevious = () => {
    const currentIndex = STEPS.findIndex(step => step.id === currentStep);

    if (currentIndex > 0) {
      setCurrentStep(STEPS[currentIndex - 1].id as SkatingPassStep);
      // Scroll to top for mobile users
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  const handleStepComplete = (stepData: Partial<SkatingPassData>) => {
    setPassData(prev => {
      const updatedData = { ...prev, ...stepData };

      // Update skater info based on selected pass type
      if (updatedData.selectedPassType) {
        updatedData.skaterInfo = getUserSkaterInfo(
          updatedData.selectedPassType,
        );
      }

      return updatedData;
    });
  };

  const processPayment = (stepData: Partial<SkatingPassData>) => {
    const updatedData = { ...passData, ...stepData };
    setPassData(updatedData);

    console.log('Updated pass data with payment info:', updatedData);

    setCurrentStep('confirmation');
    // Scroll to top for mobile users
    window.scrollTo({ top: 0, behavior: 'smooth' });

    submitPassBookingDataWithData(updatedData);
  };

  const submitPassBookingDataWithData = async (data: SkatingPassData) => {
    setIsSubmitting(true);
    setSubmitError('');

    try {
      // Create FormData to send to the API
      const formData = new FormData();

      // Option 1: Send as JSON string (current approach)
      formData.append(
        'bookingData',
        JSON.stringify({
          barcodeId: data.barcodeId,
          selectedPass: data.selectedPass,
          selectedPassType: data.selectedPassType,
          selectedPrice: data.selectedPrice,
          participantsInfo: data.participantsInfo,
          waiverAccepted: data.waiverAccepted,
          skaterWaivers: data.skaterWaivers,
          isSkatingWaiverAttached: data.isSkatingWaiverAttached,
          skaterWaiverAttached: data.skaterWaiverAttached,
          attachedWaivers: data.attachedWaivers,
          paymentInfo: data.paymentInfo,
          submittedAt: new Date().toISOString(),
        }),
      );

      // APPROACH 1: File Objects (Recommended for most cases)
      // Pros:
      // - Smaller payload size (files are compressed)
      // - Better for large files
      // - Standard way to handle file uploads
      // - Browser handles file encoding automatically
      // Cons:
      // - Requires multipart/form-data
      // - Server needs to handle file uploads
      for (const waiver of data.skaterWaivers) {
        if (waiver.signedPDF) {
          formData.append(
            'signedPDFs',
            waiver.signedPDF,
            `${waiver.skaterName}_waiver.pdf`,
          );
        }
      }

      // Submit to API using TicketService
      const response =
        await SkatingPassService.createSkatingPassBooking(formData);

      console.log('Ticket booking submitted successfully:', response);

      setIsSubmitted(true);
    } catch (error: any) {
      console.error('Error submitting pass booking:', error);
      setSubmitError(
        error?.detail ||
          error?.message ||
          'Failed to submit pass booking. Please try again.',
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  const renderCurrentStep = () => {
    switch (currentStep) {
      case 'selectSkatingPass':
        return (
          <SelectSkatingPass
            selectedPass={passData.selectedPass}
            selectedPassType={passData.selectedPassType}
            onNext={handleNext}
            onStepComplete={handleStepComplete}
          />
        );
      case 'participants':
        return (
          <ParticipantsInfo
            participantsInfo={passData.participantsInfo}
            onNext={handleNext}
            onPrevious={handlePrevious}
            onStepComplete={handleStepComplete}
            hideAddButton={true}
            maxAge={
              passData.selectedPassType?.toLowerCase() === 'child'
                ? 13
                : undefined
            }
            minAge={
              passData.selectedPassType?.toLowerCase() === 'child' ? 3 : 13
            }
          />
        );
      case 'waiver':
        return (
          <WaiverNew
            skatersInfo={passData.participantsInfo?.map(p => ({
              ...p,
              email: '',
              phoneNumber: '',
              isMinor: false,
              isAdult: true,
              isChild: false,
            }))}
            onPrevious={handlePrevious}
            isInFlow={true}
            onContinue={waiverData => {
              // Check if ONLY existing waivers were attached (no new form filled)
              if (waiverData.isExistingWaiver && !waiverData.hasMixedWaivers) {
                // Use the attached waivers directly
                handleNext({
                  waiverAccepted: true,
                  isSkatingWaiverAttached: true,
                  attachedWaivers: waiverData.attachedWaivers,
                });
                return;
              }

              // Handle mixed scenario or new waiver only
              const skaterWaivers = [];

              // Add new waiver data if it exists (from filled form)
              if (waiverData.adult && waiverData.signedPDF) {
                // Add adult waiver
                const adultWaiver = {
                  skaterId: '1',
                  skaterName: `${waiverData.adult.firstName} ${waiverData.adult.lastName}`,
                  dateOfBirth: waiverData.adult.dateOfBirth,
                  isAdult: true,
                  hasReadWaiver: waiverData.adult.hasReadWaiver,
                  accepted: waiverData.adult.accepted,
                  signature: waiverData.adult.signature,
                  signedBy: waiverData.adult.signedBy,
                  relationship: 'Self',
                  email: waiverData.adult.email,
                  phoneNumber: waiverData.adult.phoneNumber,
                  emergencyContactPhone: waiverData.adult.emergencyContactPhone,
                  signedPDF: waiverData.signedPDF,
                  barcodeId: waiverData.barcodeId,
                  minors: waiverData.minors.map(
                    (_m: any, idx: number) => `minor-${idx + 2}`,
                  ),
                  waiverId: waiverData.waiverId,
                };
                skaterWaivers.push(adultWaiver);

                // Add minor waivers (they reference the adult's PDF)
                waiverData.minors.forEach((minor: any, idx: number) => {
                  const minorWaiver = {
                    skaterId: `minor-${idx + 2}`,
                    skaterName: `${minor.firstName} ${minor.lastName}`,
                    dateOfBirth: minor.dateOfBirth,
                    isAdult: false,
                    hasReadWaiver: waiverData.adult.hasReadWaiver,
                    accepted: waiverData.adult.accepted,
                    signature: waiverData.adult.signature,
                    signedBy: waiverData.adult.signedBy,
                    relationship: 'Guardian',
                    email: waiverData.adult.email,
                    phoneNumber: waiverData.adult.phoneNumber,
                    emergencyContactPhone:
                      waiverData.adult.emergencyContactPhone,
                    signedPDF: waiverData.signedPDF,
                    minors: [],
                    barcodeId: waiverData.barcodeId,
                    waiverId: waiverData.waiverId,
                  };
                  skaterWaivers.push(minorWaiver);
                });
              }

              handleNext({
                waiverAccepted: true,
                ...(skaterWaivers.length > 0 && { skaterWaivers }),
                isSkatingWaiverAttached:
                  waiverData.attachedWaivers !== null &&
                  waiverData.attachedWaivers !== undefined,
                attachedWaivers: waiverData.attachedWaivers,
              });
            }}
          />
        );
      case 'payment':
        return (
          <Payment
            paymentType="passes" // Using season type for passes
            bookingData={passData}
            onPrevious={handlePrevious}
            onStepComplete={processPayment}
          />
        );
      case 'confirmation':
        return (
          <BookingSubmissionStatus
            isSubmitting={isSubmitting}
            isSubmitted={isSubmitted}
            submitError={submitError}
            onTryAgain={() => setCurrentStep('payment')}
            onStartOver={() => setCurrentStep('selectSkatingPass')}
          >
            <PassConfirmation
              passData={passData}
              passNumber={passData.barcodeId}
            />
          </BookingSubmissionStatus>
        );
      default:
        return null;
    }
  };

  return (
    <div className="max-w-4xl mx-auto p-0 md:p-6">
      {/* Progress Indicator */}
      <ProgressIndicator steps={STEPS} currentStep={currentStep} />

      {/* Current Step Content */}
      <div className="bg-white rounded-lg shadow-lg p-2 md:p-6">
        {renderCurrentStep()}
      </div>

      {/* Debug: Show waiver data */}
      {process.env.NODE_ENV === 'development' && (
        <div className="mt-4 p-4 bg-gray-100 rounded">
          <h3 className="text-sm font-medium text-gray-700 mb-2">
            Debug Data:
          </h3>
          <pre className="text-xs text-gray-600 overflow-auto">
            {JSON.stringify(passData, null, 2)}
          </pre>
        </div>
      )}
    </div>
  );
};

export default SkatingPassFlow;
